import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";
import { createNotificationSocket } from "../lib/notificationSocket";
import { apiGetInbox, getAccessToken } from "../lib/api";

const NotificationContext = createContext(null);

const WS_URL = process.env.REACT_APP_WS_URL;

// Cursor is per-user: two accounts on one browser must not inherit each other's
// read position, or the second would silently skip the first's backlog.
const cursorKey = (email) => `notif_cursor:${email}`;

/**
 * Owns the notification connection for the signed-in user.
 *
 * Delivery is two paths that together make it reliable:
 *
 *  - **The socket** carries anything that arrives while a tab is open.
 *  - **The inbox** fills every gap. It is called on each (re)connect, which is
 *    what covers the tab being closed, the laptop sleeping, a background tab
 *    being throttled, and the 2-hour connection recycle.
 *
 * Neither alone is sufficient — the socket misses everything sent while closed,
 * and the inbox alone would be polling.
 */
export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const { toast } = useToast();

  const [connectionState, setConnectionState] = useState("disconnected");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const socketRef = useRef(null);
  const cursorRef = useRef(null);
  const seenRef = useRef(new Set());

  const configured = Boolean(WS_URL);
  const email = user?.email || null;

  /**
   * Add notifications, newest first, ignoring anything already shown.
   *
   * De-duplication matters because the socket and the inbox legitimately
   * overlap: a notification can arrive live and again in the next catch-up if
   * the cursor write and the delivery race. Ids are unique per notification, so
   * the set is authoritative.
   */
  const ingest = useCallback((incoming, { notify }) => {
    const fresh = incoming.filter((n) => n.id && !seenRef.current.has(n.id));
    if (fresh.length === 0) return;

    fresh.forEach((n) => seenRef.current.add(n.id));
    setNotifications((prev) => [...fresh].reverse().concat(prev).slice(0, 100));
    setUnreadCount((prev) => prev + fresh.length);

    if (notify) {
      fresh.forEach((n) =>
        toast({
          title: n.header || "Notification",
          description: n.content || "",
          variant: "default",
          duration: 8000,
        })
      );
    }

    // Advance the cursor past everything ingested. Ids sort chronologically, so
    // the maximum is the newest.
    const newest = fresh.reduce((max, n) => (n.id > max ? n.id : max), cursorRef.current || "");
    if (newest && newest !== cursorRef.current) {
      cursorRef.current = newest;
      if (email) {
        try { localStorage.setItem(cursorKey(email), newest); } catch { /* quota/private mode */ }
      }
    }
  }, [toast, email]);

  /** Pull everything missed since the stored cursor. */
  const catchUp = useCallback(async () => {
    try {
      const data = await apiGetInbox({ since: cursorRef.current || undefined });
      if (data?.notifications?.length) {
        // Toast the backlog too — it is news to the user either way — but the
        // toast system caps what is visible, so a large backlog degrades to the
        // bell count rather than a wall of toasts.
        ingest(data.notifications, { notify: data.notifications.length <= 3 });
      } else if (data?.next_since) {
        cursorRef.current = data.next_since;
      }
    } catch (err) {
      // A failed catch-up must not break the session. The next reconnect
      // retries, and reconnects are frequent.
      console.warn("[notifications] catch-up failed —", err?.message || err);
    }
  }, [ingest]);

  useEffect(() => {
    if (!email || !configured) return;

    cursorRef.current = (() => {
      try { return localStorage.getItem(cursorKey(email)); } catch { return null; }
    })();
    seenRef.current = new Set();

    const socket = createNotificationSocket({
      url: WS_URL,
      getToken: getAccessToken,
      onStateChange: setConnectionState,
      onOpen: catchUp,
      onMessage: (payload) => ingest([payload], { notify: true }),
    });

    socketRef.current = socket;
    socket.start();

    return () => {
      socket.stop();
      socketRef.current = null;
    };
  }, [email, configured, catchUp, ingest]);

  // Sign-out: drop everything in memory. The cursor stays in localStorage so the
  // same user does not replay their history on the next sign-in.
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      seenRef.current = new Set();
    }
  }, [user]);

  const markAllRead = useCallback(() => setUnreadCount(0), []);

  const value = useMemo(
    () => ({
      supported: configured,
      connected: connectionState === "connected",
      connectionState,
      notifications,
      unreadCount,
      markAllRead,
    }),
    [configured, connectionState, notifications, unreadCount, markAllRead]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used inside NotificationProvider");
  return ctx;
}
